import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "@/api/client";
import { Button } from "@/components/ui/Button";
import { invalidateNutritionQueries } from "@/lib/nutritionQueries";
import type { DiaryCompleteness } from "@/types";

export function DiaryCompletenessCard({ date, completeness, compact = false }: {
  date: string;
  completeness?: DiaryCompleteness;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const complete = completeness?.status === "complete";
  const change = useMutation({
    mutationFn: (status: "complete" | "partial") => api.put(`/nutrition/day/${date}/completeness`, {
      status, ...(status === "complete" ? { revision: completeness?.revision } : {}),
    }),
    onSuccess: () => invalidateNutritionQueries(client),
    onError: () => invalidateNutritionQueries(client),
  });
  const conflict = (change.error as { response?: { status?: number } } | null)?.response?.status === 409;
  return (
    <div className="my-3 rounded-lg border border-cream-border bg-cream-light p-3 text-[12px]" data-testid="diary-completeness">
      <p className="font-medium text-navy-deep">{t(complete ? "diary_quality.complete" : "diary_quality.partial")}</p>
      {!compact && <p className="mt-1 text-taupe">{t(completeness?.reason === "diary_changed" ? "diary_quality.changed" : "diary_quality.explanation")}</p>}
      {!compact && completeness?.can_edit && (
        <Button className="mt-2" size="sm" variant="ghost" loading={change.isPending}
          onClick={() => change.mutate(complete ? "partial" : "complete")}>
          {t(complete ? "diary_quality.reopen" : "diary_quality.confirm")}
        </Button>
      )}
      {change.isError && <p className="mt-2 text-coral" role="alert">{t(conflict ? "diary_quality.conflict" : "diary_quality.error")}</p>}
    </div>
  );
}
